// KAN-156: global listener that plays the "position filled" chime and shows a
// clickable toast for school admins (owner + co-admins), on any page of the app.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, VolumeX } from 'lucide-react';
import { getStoredToken, getStoredUser } from '@/lib/auth';
import { playPositionFilledSound, unlockAudio } from '@/lib/audioSettings';
import {
  PREFS_CHANGED_EVENT,
  fetchNotificationPrefs,
  getCachedNotificationPrefs,
  syncPositionFilledSoundFromServer,
} from '@/lib/notificationPrefs';
import { usePositionFilledEvents } from '@/hooks/usePositionFilledEvents';

export const POSITION_FILLED_WINDOW_EVENT = 'expresssub:position-filled';

// Cross-tab dedupe: every open tab shows the toast, but only ONE tab plays the
// chime. Each tab writes a claim for the event_id, waits briefly, and plays only
// if its claim survived (last writer wins; all tabs read the same final value).
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const CLAIM_PREFIX = 'expresssub_pf_sound_';
const CLAIM_SETTLE_MS = 120;

const claimSoundForThisTab = async (eventId) => {
  const key = `${CLAIM_PREFIX}${eventId}`;
  try {
    // Tabs the admin hasn't interacted with may be autoplay-blocked: let tabs
    // with user activation (audio unlocked) claim first.
    const activated = navigator.userActivation ? navigator.userActivation.hasBeenActive : true;
    if (!activated) await new Promise((r) => setTimeout(r, CLAIM_SETTLE_MS * 2));
    if (localStorage.getItem(key)) return false; // another tab already claimed it
    localStorage.setItem(key, TAB_ID);
    await new Promise((r) => setTimeout(r, CLAIM_SETTLE_MS));
    const owner = localStorage.getItem(key);
    setTimeout(() => { try { localStorage.removeItem(key); } catch (_e) { /* ignore */ } }, 5 * 60 * 1000);
    return owner === TAB_ID;
  } catch (_e) {
    return true; // storage unavailable: just play
  }
};

export const formatPositionFilledMessage = (evt) =>
  `Position Filled: ${evt.position_title || `Position at ${evt.school_name || 'your school'}`} on ${evt.date_display || 'the scheduled date'} has been accepted by ${evt.sub_name || 'a Sub'}.`;

export function positionDetailsPath(evt) {
  const params = new URLSearchParams({ position: evt.position_id });
  if (evt.school_id) params.set('school', evt.school_id);
  return `/employer?${params.toString()}`;
}

export default function PositionFilledListener() {
  const location = useLocation();
  const navigate = useNavigate();
  // Re-read auth on navigation so login/logout immediately (dis)connects.
  const [auth, setAuth] = useState(() => ({ user: getStoredUser(), token: getStoredToken() }));
  const soundOnRef = useRef(getCachedNotificationPrefs().position_filled_sound);
  const blockedHintShown = useRef(false);

  useEffect(() => {
    const user = getStoredUser();
    const token = getStoredToken();
    setAuth((prev) => (prev.token === token && prev.user?.user_id === user?.user_id ? prev : { user, token }));
  }, [location.pathname]);

  const isSchoolAdmin = auth.user?.role === 'employer' && !!auth.token;

  // Load account preference once per session and follow live changes.
  useEffect(() => {
    if (!isSchoolAdmin) return undefined;
    fetchNotificationPrefs()
      .then((p) => { soundOnRef.current = p.position_filled_sound; })
      .catch(() => { /* keep cached/default */ });
    const onChange = (e) => { soundOnRef.current = e.detail?.position_filled_sound !== false; };
    window.addEventListener(PREFS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(PREFS_CHANGED_EVENT, onChange);
  }, [isSchoolAdmin, auth.user?.user_id]);

  // Autoplay policy: unlock audio on the first interaction so later chimes play.
  useEffect(() => {
    if (!isSchoolAdmin) return undefined;
    const unlock = () => unlockAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [isSchoolAdmin]);

  const handleEvent = useCallback(async (evt) => {
    // Let open pages (e.g. the dashboard) refresh their data.
    window.dispatchEvent(new CustomEvent(POSITION_FILLED_WINDOW_EVENT, { detail: evt }));

    const goToDetails = () => navigate(positionDetailsPath(evt));
    toast.custom((t) => (
      <button
        type="button"
        onClick={() => { toast.dismiss(t); goToDetails(); }}
        className="w-full sm:w-[356px] text-left flex items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4 shadow-lg hover:bg-emerald-50/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        data-testid="position-filled-toast"
        data-position-id={evt.position_id}
      >
        <span className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm text-slate-800 leading-snug" data-testid="position-filled-toast-text">
            {formatPositionFilledMessage(evt)}
          </span>
          <span className="mt-1 inline-flex items-center text-xs font-medium text-emerald-700">
            View position details <ChevronRight className="h-3 w-3 ml-0.5" />
          </span>
        </span>
      </button>
    ), { duration: 10000, id: `position-filled-${evt.event_id}` });

    // Server payload carries the latest account preference (other device may have changed it).
    syncPositionFilledSoundFromServer(evt.play_sound);
    const shouldPlay = typeof evt.play_sound === 'boolean' ? evt.play_sound : soundOnRef.current;
    if (!shouldPlay) return;
    if (!(await claimSoundForThisTab(evt.event_id))) return;

    const played = await playPositionFilledSound();
    if (!played && !blockedHintShown.current) {
      // Graceful fallback: the toast already informed the admin; explain the silence once.
      blockedHintShown.current = true;
      toast('Sound alerts are paused by your browser', {
        description: 'Click anywhere on the page to enable the position-filled chime.',
        icon: <VolumeX className="h-4 w-4" />,
        duration: 8000,
        id: 'position-filled-audio-blocked',
      });
    }
  }, [navigate]);

  usePositionFilledEvents({
    enabled: isSchoolAdmin,
    token: auth.token,
    onEvent: handleEvent,
    onModeChange: (mode) => { document.documentElement.dataset.positionEvents = mode; },
  });

  return null;
}
